import * as React from "react";
import { tv } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { CloseIcon } from "./icons/CloseIcon";
import { NotificationBannerClient } from "./NotificationBannerClient";

const notificationBannerContainer = tv({
  base: cn(
    "uipm:w-full",
    "uipm:min-h-[58px]",
    "uipm:bg-[var(--surface-surface-accent-secondary)]",
    "uipm:transition-all uipm:duration-300",
    "uipm:fixed uipm:top-0 uipm:left-0 uipm:right-0 uipm:z-[70]",
  ),
  variants: {
    isVisible: {
      true: "uipm:opacity-100 uipm:translate-y-0",
      false: "uipm:opacity-0 uipm:-translate-y-full uipm:pointer-events-none",
    },
  },
  defaultVariants: {
    isVisible: true,
  },
});

const innerContainer = tv({
  base: cn(
    "uipm:max-w-[1440px] uipm:mx-auto",
    "uipm:px-[var(--container-margin)]",
    "uipm:py-3",
  ),
});

const contentWrapper = tv({
  base: cn(
    "uipm:h-full",
    "uipm:flex uipm:items-center uipm:justify-between uipm:gap-4",
  ),
});

const textContainer = tv({
  base: cn(
    "uipm:flex-1",
    "uipm:text-[var(--typography-colors-text-accent)]",
    "uipm:font-[var(--font-family-sans)]",
    "uipm:font-medium",
    "uipm:fz-[var(--typography-sizes-body-medium)]",
    "uipm:font-normal",
    "uipm:leading-[var(--typography-line-heights-body-medium)]",
    "[&_a]:uipm:text-[var(--typography-colors-text-accent)]!",
    "[&_a]:uipm:underline!",
    "[&_a]:uipm:decoration-[var(--typography-colors-text-accent)]!",
    "[&_a]:uipm:underline-offset-2",
    "[&>a]:uipm:underline!",
    "[&>p>a]:uipm:underline!",
    "[&_p]:uipm:m-0",
    "[&_strong]:uipm:font-semibold",
  ),
});

const closeButton = tv({
  base: cn(
    "uipm:flex uipm:items-center uipm:justify-center",
    "uipm:p-2",
    "uipm:rounded-[var(--radius-sm)]",
    "uipm:transition-colors uipm:duration-200",
    "uipm:cursor-pointer",
    "hover:uipm:bg-[var(--surface-surface-secondary)]",
    "uipm:text-[var(--typography-colors-text-accent)]",
  ),
});

export interface NotificationBannerProps
  extends Omit<ComponentProps<"div">, "children"> {
  children?: React.ReactNode;
  storageKey?: string;
  onClose?: () => void;
}

export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  children,
  storageKey = "notification-banner-dismissed",
  onClose,
  className,
  ...props
}) => {
  return (
    <NotificationBannerClient storageKey={storageKey}>
      {({ isVisible, bannerRef, handleClose }) => (
        <div
          ref={bannerRef}
          data-notification-banner
          className={cn(notificationBannerContainer({ isVisible }), className)}
          role="banner"
          aria-live="polite"
          {...props}
        >
          <div className={innerContainer()}>
            <div className={contentWrapper()}>
              <div className={textContainer()}>{children}</div>
              <button
                type="button"
                className={closeButton()}
                onClick={() => {
                  handleClose();
                  onClose?.();
                }}
                aria-label="Close notification"
              >
                <CloseIcon size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationBannerClient>
  );
};
